import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItem: any;
  refetch: () => void;
}

export const ReturnDialog = ({
  open,
  onOpenChange,
  selectedItem,
  refetch,
}: ReturnDialogProps) => {
  const [returnQuantity, setReturnQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [condition, setCondition] = useState("bon");
  const [hasRefund, setHasRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const handleConfirm = async () => {
    try {
      if (!returnQuantity || parseInt(returnQuantity) <= 0) {
        toast.error("Veuillez entrer une quantité valide");
        return;
      }

      if (!reason) {
        toast.error("Veuillez indiquer la raison du retour");
        return;
      }

      if (hasRefund) {
        if (!refundAmount || parseFloat(refundAmount) <= 0) {
          toast.error("Veuillez entrer un montant de remboursement valide");
          return;
        }

        if (!paymentMethod) {
          toast.error("Veuillez sélectionner un mode de remboursement");
          return;
        }
      }

      const quantity = parseInt(returnQuantity);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Utilisateur non connecté");
        return;
      }

      // Calculer le montant unitaire à partir des données du produit
      const unitPrice = selectedItem?.data?.price ? parseFloat(selectedItem.data.price) : 0;
      const totalAmount = quantity * unitPrice;

      // Créer la transaction de retour
      const { error: transactionError } = await supabase
        .from('poultry_transactions')
        .insert({
          stock_item_id: selectedItem.id,
          transaction_type: 'return',
          quantity,
          unit_price: unitPrice,
          total_amount: totalAmount,
          transaction_date: new Date().toISOString(),
          notes: reason,
          created_by: user.id,
          refund_amount: hasRefund ? parseFloat(refundAmount) || 0 : 0,
          refund_payment_method: hasRefund ? paymentMethod : null,
          return_condition: condition
        });

      if (transactionError) throw transactionError;

      // Mettre à jour la quantité en stock
      const { error: updateError } = await supabase
        .from('stock_items')
        .update({ 
          quantity: selectedItem.quantity + quantity,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedItem.id);

      if (updateError) throw updateError;

      toast.success("Retour enregistré avec succès");
      onOpenChange(false);
      refetch();
    } catch (error: any) {
      console.error("Erreur lors du retour:", error);
      toast.error(error.message || "Erreur lors du retour");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Retour de produit - {selectedItem?.data?.name}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="quantity">Quantité retournée</Label>
            <Input
              id="quantity"
              type="number"
              value={returnQuantity}
              onChange={(e) => setReturnQuantity(e.target.value)}
              min="1"
              max={selectedItem?.quantity}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="condition">État du produit</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez l'état" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bon">Bon état</SelectItem>
                <SelectItem value="abîmé">Abîmé</SelectItem>
                <SelectItem value="défectueux">Défectueux</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reason">Raison du retour</Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Expliquez la raison du retour"
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="refund"
              checked={hasRefund}
              onChange={(e) => setHasRefund(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="refund">Effectuer un remboursement</Label>
          </div>
          {hasRefund && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="refundAmount">Montant du remboursement</Label>
                <Input
                  id="refundAmount"
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  min="0"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="paymentMethod" className="flex items-center">
                  Mode de remboursement <span className="text-red-500 ml-1">*</span>
                </Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez le mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Espèces">Espèces</SelectItem>
                    <SelectItem value="Wave">Wave</SelectItem>
                    <SelectItem value="Orange Money">Orange Money</SelectItem>
                    <SelectItem value="Virement bancaire">Virement bancaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleConfirm}>
            Confirmer le retour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};